<script>
import clickOutside from '~/directives/clickOutside'

export default {
    directives: {
        clickOutside
    }
}
</script>

<template>
    <div class="min-h-screen bg-gray-50">
        <!-- Mobile Menu Toggle -->
        <button @click.stop="toggleSidebar"
            class="md:hidden fixed top-4 right-4 p-2 bg-white rounded-lg shadow-lg z-50">
            <AppIcon3Bars v-if="!isSidebarOpen" class="w-6 h-6 text-cost-red" />
            <AppIconXMark v-else class="w-6 h-6 text-cost-red" />
        </button>

        <!-- Sidebar Overlay -->
        <div v-if="isSidebarOpen" @click="closeSidebar" class="fixed inset-0 z-40 bg-black/50 md:hidden"></div>

        <!-- Sidebar -->
        <aside v-click-outside="closeSidebarOnMobile"
            class="fixed h-full bg-white shadow-lg z-50 w-64 transition-transform duration-300"
            :class="isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'">
            <div class="p-4 border-b border-gray-200">
                <NuxtLink to="/" class="flex items-center justify-center" @click="closeSidebar">
                    <AppLogo />
                </NuxtLink>
            </div>

            <nav class="p-4 space-y-2">
                <NuxtLink v-for="link in navLinks" :key="link.path" :to="link.path"
                    class="flex items-center p-3 rounded-lg hover:bg-cost-red/10 text-gray-700 hover:text-cost-red"
                    @click="closeSidebar">
                    <component :is="link.icon" class="w-5 h-5 mr-3" />
                    {{ link.label }}
                </NuxtLink>
            </nav>

            <!-- User Profile Dropdown -->
            <div class="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
                <Menu as="div" class="relative">
                    <MenuButton class="w-full flex items-center space-x-3 hover:bg-gray-100 p-2 rounded-lg">
                        <div class="w-8 h-8 bg-cost-red text-white rounded-full flex items-center justify-center">
                            {{ userInitials }}
                        </div>
                        <div class="text-left">
                            <p class="text-sm font-medium">{{ currentUser.name }}</p>
                            <p class="text-xs text-gray-500">{{ currentUser.email }}</p>
                            <p class="text-xs text-gray-500">{{ currentUser.role }}</p>
                        </div>
                    </MenuButton>

                    <MenuItems
                        class="absolute left-4 bottom-full mb-2 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div class="p-2">
                            <MenuItem v-slot="{ active }">
                            <NuxtLink to="/account"
                                :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 rounded-lg']"
                                @click="closeSidebar">
                                Update Account
                            </NuxtLink>
                            </MenuItem>
                            <MenuItem v-slot="{ active }">
                            <button @click="logout"
                                :class="[active ? 'bg-gray-100' : '', 'w-full text-left px-4 py-2 text-sm text-gray-700 rounded-lg']">
                                Sign Out
                            </button>
                            </MenuItem>
                        </div>
                    </MenuItems>
                </Menu>
            </div>
        </aside>

        <!-- Main Content -->
        <main class="pt-20 md:pt-4 px-4 md:ml-64 transition-all duration-300">
            <div class="max-w-7xl mx-auto">
                <slot />
            </div>
        </main>
    </div>
</template>

<script setup>
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { AppIconCurrencyDollar, AppIconChartPie, AppIconDocumentText, AppIconExclamationTriangle } from '#components'

const supabase = useSupabaseClient()
const user = useSupabaseUser()

// Fetch user profile
const { data } = await supabase
    .from('profiles')
    .select(`role`)
    .eq('id', user.value.id)
    .single()


const currentUser = ref({
    name: data?.username || user.value.email,
    email: user.value.email,
    role: data?.role || null,
})

const isSidebarOpen = ref(false)
const route = useRoute()

const navLinks = [
    { label: 'Dashboard', path: '/dashboard', icon: AppIconChartPie },
    { label: 'Receipts', path: '/receipts', icon: AppIconDocumentText },
    { label: 'Price Adjustments', path: '/price-adjustments', icon: AppIconCurrencyDollar },
    // { label: 'Settings', path: '/settings', icon: AppIconCog },
    // AppIconExclamationTriangle
    { label: 'Recalls', path: '/recalls', icon: AppIconExclamationTriangle },
]

// Close sidebar on mobile when navigating
watch(() => route.path, () => {
    if (window.innerWidth < 768) {
        isSidebarOpen.value = false
    }
})

const userInitials = computed(() => {
    return currentUser.value?.name
        ?.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase() || 'U'
})

const toggleSidebar = () => {
    console.log('toggleSidebar')
    isSidebarOpen.value = !isSidebarOpen.value
}

const closeSidebar = () => {
    if (window.innerWidth < 768) {
        isSidebarOpen.value = false
    }
}

const closeSidebarOnMobile = (event) => {
    if (window.innerWidth < 768) {
        isSidebarOpen.value = false
    }
}

// Handle window resize
const checkScreenSize = () => {
    if (window.innerWidth >= 768) {
        isSidebarOpen.value = true
    } else {
        isSidebarOpen.value = false
    }
}

onMounted(() => {
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', checkScreenSize)
})

const logout = async () => {
    try {
        // loading.value = true
        const { error } = await supabase.auth.signOut()
        if (error) throw error
        // clear user data
        user.value = null
        // navigate to login page
        navigateTo('/auth/login')
    } catch (error) {
        // todo: handle error
        alert(error.message)
    } finally {
        // loading.value = false
    }
}
</script>

<style scoped>
.router-link-active {
    @apply bg-cost-red/10 text-cost-red font-medium;
}

.aside {
    @apply fixed h-full bg-white shadow-lg z-50 w-64 transition-transform duration-300;
}

@media (min-width: 768px) {
    .aside {
        transform: translateX(0) !important;
    }
}
</style>